/**
 * Storage utility for pixelmuse.ai
 * Handles favorites and API caching with proper namespacing
 */

// Constants
const FAVORITES_KEY = 'pixelmuse:favorites';
const CACHE_PREFIX = 'pixelmuse:cache:';
export const MAX_FAVORITES = 450; // Hard limit for favorites

// Simple hash function for URLs
const hashString = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash).toString(36);
};

// Favorites management
export const favoritesStorage = {
  // Get all favorites
  getAll: () => {
    try {
      const favorites = localStorage.getItem(FAVORITES_KEY);
      return favorites ? JSON.parse(favorites) : [];
    } catch (e) {
      console.error('Error retrieving favorites:', e);
      return [];
    }
  },

  // Check if a term is favorited
  isFavorite: (term) => {
    return favoritesStorage.getAll().includes(term);
  },

  // Add a term to favorites
  add: (term) => {
    const favorites = favoritesStorage.getAll();
    if (!favorites.includes(term)) {
      // Check if we've reached the limit
      if (favorites.length >= MAX_FAVORITES) {
        console.warn(`Cannot add favorite: maximum limit of ${MAX_FAVORITES} reached`);
        return false;
      }
      favorites.push(term);
      localStorage.setItem(FAVORITES_KEY, JSON.stringify(favorites));
      return true;
    }
    return false;
  },

  // Remove a term from favorites
  remove: (term) => {
    const favorites = favoritesStorage.getAll();
    const index = favorites.indexOf(term);
    if (index !== -1) {
      favorites.splice(index, 1);
      localStorage.setItem(FAVORITES_KEY, JSON.stringify(favorites));
    }
  },

  // Toggle favorite status
  toggle: (term) => {
    if (favoritesStorage.isFavorite(term)) {
      favoritesStorage.remove(term);
      return false;
    } else {
      return favoritesStorage.add(term);
    }
  },

  // Export favorites as JSON string
  export: () => {
    return localStorage.getItem(FAVORITES_KEY) || '[]';
  },
  
  // Import favorites from JSON string
  import: (jsonString) => {
    try {
      const importedFavorites = JSON.parse(jsonString);
      if (Array.isArray(importedFavorites)) {
        // Get existing favorites
        const existingFavorites = favoritesStorage.getAll();
        
        // Merge favorites without duplicates
        const mergedFavorites = [...existingFavorites];
        let newItemsCount = 0;
        let reachedLimit = false;
        
        for (const term of importedFavorites) {
          if (!mergedFavorites.includes(term)) {
            // Check if we've reached the limit
            if (mergedFavorites.length >= MAX_FAVORITES) {
              reachedLimit = true;
              break;
            }
            mergedFavorites.push(term);
            newItemsCount++;
          }
        }
        
        // Save merged favorites
        localStorage.setItem(FAVORITES_KEY, JSON.stringify(mergedFavorites));
        
        // Return additional information about the import
        return {
          success: true,
          total: mergedFavorites.length,
          added: newItemsCount,
          reachedLimit: reachedLimit
        };
      }
      return { success: false };
    } catch (e) {
      console.error('Error importing favorites:', e);
      return { success: false, error: e.message };
    }
  },

  // Migrate from old storage format
  migrateFromOld: () => {
    const oldFavorites = [];
    const keys = Object.keys(localStorage);
    
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      // Check if it's an old-style favorite (key equals value and not a URL)
      if (localStorage.getItem(key) === key && 
          !key.startsWith('http://') && 
          !key.startsWith('https://') &&
          !key.includes(':')) {
        oldFavorites.push(key);
      }
    }
    
    if (oldFavorites.length > 0) {
      localStorage.setItem(FAVORITES_KEY, JSON.stringify(oldFavorites));
      
      // Clean up old entries
      oldFavorites.forEach(key => {
        localStorage.removeItem(key);
      });
      
      return oldFavorites.length;
    }
    
    return 0;
  }
};

// API cache management
export const cacheStorage = {
  // Get cached response
  get: (url) => {
    const key = CACHE_PREFIX + hashString(url);
    try {
      const cached = sessionStorage.getItem(key);
      return cached ? JSON.parse(cached) : null;
    } catch (e) {
      console.error('Error retrieving from cache:', e);
      return null;
    }
  },

  // Set cache response
  set: (url, data) => {
    const key = CACHE_PREFIX + hashString(url);
    try {
      sessionStorage.setItem(key, JSON.stringify(data));
    } catch (e) {
      console.error('Error setting cache:', e);
      // If we hit storage limits, clear the cache and try again
      cacheStorage.clearAll();
      try {
        sessionStorage.setItem(key, JSON.stringify(data));
      } catch (innerError) {
        console.error('Failed to set cache even after clearing:', innerError);
      }
    }
  },

  // Clear all cached responses
  clearAll: () => {
    const keys = Object.keys(sessionStorage);
    let count = 0;
    
    for (let i = 0; i < keys.length; i++) {
      if (keys[i].startsWith(CACHE_PREFIX)) {
        sessionStorage.removeItem(keys[i]);
        count++;
      }
    }
    
    return count;
  },

  // Migrate from old storage format (moving from localStorage to sessionStorage)
  migrateFromOld: () => {
    const keys = Object.keys(localStorage);
    let count = 0;
    
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      if (key.startsWith('http://') || key.startsWith('https://')) {
        try {
          const data = localStorage.getItem(key);
          if (data) {
            cacheStorage.set(key, JSON.parse(data));
            localStorage.removeItem(key);
            count++;
          }
        } catch (e) {
          console.error('Error migrating cache item:', e);
        }
      }
    }
    
    return count;
  }
};

// Export/Import utilities for migration between domains
export const migrationUtils = {
  // Export all favorites as a downloadable file
  exportFavorites: () => {
    // Get favorites array from storage
    const favorites = favoritesStorage.getAll();
    
    // Create a JSON blob with the favorites array
    const blob = new Blob([JSON.stringify(favorites)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    
    // Create and trigger download
    const a = document.createElement('a');
    a.href = url;
    a.download = 'pixelmuse-favorites.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    return favorites.length;
  },
  
  // Import favorites from a file
  importFavorites: (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (event) => {
        try {
          const result = favoritesStorage.import(event.target.result);
          if (result.success) {
            if (result.reachedLimit) {
              // Import the toast library at the top of the file if not already imported
              const { toast } = require('react-toastify');
              toast.warning(`Maximum limit of ${MAX_FAVORITES} favorites reached. Some items were not imported.`, {
                position: "top-right",
                autoClose: 5000,
              });
            }
            resolve({
              total: result.total,
              added: result.added,
              reachedLimit: result.reachedLimit
            });
          } else {
            reject(new Error('Invalid favorites format'));
          }
        } catch (e) {
          reject(e);
        }
      };
      
      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };
      
      reader.readAsText(file);
    });
  }
};

// Run migrations automatically when the module is imported
(() => {
  const migratedFavorites = favoritesStorage.migrateFromOld();
  if (migratedFavorites > 0) {
    console.log(`Migrated ${migratedFavorites} favorites to new storage format`);
  }
  
  const migratedCache = cacheStorage.migrateFromOld();
  if (migratedCache > 0) {
    console.log(`Migrated ${migratedCache} cache entries to session storage`);
  }
})();
